import React, { useEffect, useState } from "react";
import { LogIn, LogOut, CheckCircle2, Clock, User, FileText, Timer, Eye, CalendarCheck, Clock3, Info } from "lucide-react";
import type { AttendanceRecord, AttendanceType, Karyawan } from "../../../../controller/mobile/types";
import { todayDateOnly, timeLabel, toDateOnly } from "../../../../controller/mobile/Dateutils";
import type { Page } from "../../../../controller/mobile/types";

export interface LemburRecord {
  id: number;
  tipe_aktivitas: "lembur";
  status: string;
  nama_kegiatan: string;
  tanggal: string;
  mulai: string;
  sampai: string;
  keterangan: string;
  submittedAt: string;
  id_karyawan?: string | number;
}

export interface IzinRecord {
  id: number;
  tipe_aktivitas: string | null;
  penyetujuan_aktivitas: string;
  startDate: string;
  endDate: string;
  status: string
  keterangan: string;
  attachmentName: string;
  submittedAt: string;
  id_karyawan?: string | number;
}

interface HomePageProps {
  employee: Karyawan;
  records: AttendanceRecord[];
  izinRecords?: IzinRecord[];
  lemburRecords?: LemburRecord[];
  isLoading?: boolean; // <-- dari Home.tsx, true sampai semua data selesai fetch
  onNavigate: (page: Extract<Page, "home" | "capture" | "history" | "izin" | "izinlist" | "lembur" | "lemburlist" | "profile" | "none">) => void;
  onStartCapture: (type: AttendanceType) => void;
}

function greeting(hour: number): string {
  if (hour < 10) return "Selamat pagi";
  if (hour < 15) return "Selamat siang";
  if (hour < 18) return "Selamat sore";
  return "Selamat malam";
}

function statusBadge(status: string) {
  const s = (status || "").toLowerCase();
  if (s === "disetujui" || s === "approved" || s === "approve") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (s === "ditolak" || s === "rejected" || s === "reject") return "bg-red-100 text-red-700 border-red-200";
  return "bg-amber-100 text-amber-700 border-amber-200";
}

function formatStatus(status: string) {
  if (!status) return "-";
  const s = status.toLowerCase();
  if (s === "approved" || s === "approve") return "Disetujui";
  if (s === "rejected" || s === "reject") return "Ditolak";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function isTodayInRange(start: string, end: string): boolean {
  try {
    const today = todayDateOnly();
    const s = toDateOnly(start);
    const e = toDateOnly(end);
    if (!s || !e) return false;
    return today >= s && today <= e;
  } catch {
    return false;
  }
}

export default function HomePage({
  employee,
  records,
  izinRecords = [],
  lemburRecords = [],
  isLoading = false,
  onNavigate,
  onStartCapture,
}: HomePageProps): React.ReactElement {
  const [now, setNow] = useState<Date>(new Date());

  const hasEmployeeName = typeof employee?.nama === "string" && employee.nama.trim().length > 0;
  const firstName = hasEmployeeName ? employee.nama.trim().split(/\s+/)[0] : "";

  // LOADING FINAL: true kalau parent masih loading ATAU nama belum muncul
  // Parent isLoading sudah mencakup absensi + izin + lembur karena di Home.tsx pakai Promise.all
  const isPageLoading = isLoading || !hasEmployeeName;

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const todayRecord =
    records.find((r) => {
      const recordTime = r.jam_masuk ?? r.jam_keluar;
      return (
        recordTime !== null &&
        recordTime !== undefined &&
        toDateOnly(recordTime) === todayDateOnly() &&
        String(r.id_karyawan) === String(employee.id)
      );
    }) ?? null;

  const isIzinOrAlfa = todayRecord?.status === "izin" || todayRecord?.status === "alfa";
  const isPending = todayRecord?.isPending === true;
  const belumMasuk = !todayRecord;
  const sudahMasukBelumPulang = todayRecord && !todayRecord.jam_keluar && !isIzinOrAlfa;
  const sudahSelesai = todayRecord && todayRecord.jam_keluar && !isIzinOrAlfa;

  const filteredIzin = izinRecords.filter(r => {
    if (r.id_karyawan) return String(r.id_karyawan) === String(employee.id);
    return true;
  });

  const todayIzinList = filteredIzin
    .filter(r => isTodayInRange(r.startDate, r.endDate))
    .sort((a,b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());

  const filteredLembur = lemburRecords.filter(r => {
    if (r.id_karyawan) return String(r.id_karyawan) === String(employee.id);
    return true;
  });

  const todayLemburList = filteredLembur
    .filter(r => toDateOnly(r.tanggal) === todayDateOnly())
    .sort((a,b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());

  const timeString = now.toLocaleTimeString("id-ID", { hour12: false });
  const dateString = now.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 rounded-b-3xl bg-slate-900 px-6 pb-6 pt-8 text-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {isPageLoading ? (
              <>
                <div className="h-3 w-24 animate-pulse rounded bg-slate-700" />
                <div className="mt-2 h-3 w-40 animate-pulse rounded bg-slate-700" />
              </>
            ) : (
              <>
                <p className="text-xs font-medium uppercase tracking-widest text-slate-400">
                  {greeting(now.getHours())}, {firstName}
                </p>
                <p className="text-xs text-slate-400">
                  {employee.jabatan ? `${employee.jabatan}` : "Jabatan Belum Di Berikan"}
                  {employee.no_kary ? ` · ${employee.no_kary}` : "NIK Belum Di Berikan"}
                </p>
              </>
            )}
          </div>
          <button type="button" className="ml-2 rounded-full bg-slate-700 p-2 transition-colors hover:bg-slate-600" title="Buka profil" onClick={() => onNavigate("profile")}>
            <User className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-2 font-mono text-4xl font-semibold tabular-nums tracking-tight">{timeString}</p>
        <p className="mt-1 text-sm text-slate-300">{dateString}</p>
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
        {isPageLoading ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <Clock className="h-6 w-6 animate-spin text-slate-500" />
            </div>
            <p className="text-sm font-medium text-slate-800">Memuat data absensi, izin & lembur...</p>
            <p className="mt-1 text-xs text-slate-500">Menunggu {firstName ? firstName : "data karyawan"} lengkap</p>
          </section>
        ) : (
          <>
            {/* {(todayIzinList[0]?.penyetujuan_aktivitas !== "approved") && belumMasuk ? (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <Clock className="h-6 w-6 text-slate-500" />
                </div>
                <p className="text-sm font-medium text-slate-800">Kamu belum absen masuk hari ini</p>
                <button type="button" onClick={() => onStartCapture("masuk")} className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-sm font-semibold text-white">
                  <LogIn className="h-4 w-4" /> Absen Masuk
                </button>
              </section>
            ) : (
                <section className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                    <Info className="h-6 w-6 text-slate-500" />
                  </div>
                  <p className="text-sm font-medium text-slate-800">Data Absensi tidak terserta</p>
                </section>
              )
            } */}

            {belumMasuk && todayIzinList[0]?.penyetujuan_aktivitas !== "approved" && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <Clock className="h-6 w-6 text-slate-500" />
                </div>
                <p className="text-sm font-medium text-slate-800">Kamu belum absen masuk hari ini</p>
                <button type="button" onClick={() => onStartCapture("masuk")} className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-sm font-semibold text-white">
                  <LogIn className="h-4 w-4" /> Absen Masuk
                </button>
              </section>
            )} 

            {sudahMasukBelumPulang && todayRecord && (
              <section className="space-y-3">
                <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <div className="text-sm">
                    <p className="font-medium text-emerald-800">Absen masuk tercatat</p>
                    <p className="text-emerald-700">Jam {timeLabel(todayRecord.jam_masuk)}</p>
                    {isPending && <p className="mt-1 text-[11px] text-slate-600">Menunggu konfirmasi backend.</p>}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
                  <button type="button" onClick={() => onStartCapture("pulang")} className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-sm font-semibold text-white">
                    <LogOut className="h-4 w-4" /> Absen Pulang
                  </button>
                </div>
              </section>
            )}

            {sudahSelesai && todayRecord && (
              <section className="space-y-3">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  </div>
                  <p className="text-sm font-semibold text-emerald-800">Absensi hari ini selesai</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs text-slate-500">Masuk</p>
                    <p className="mt-1 font-mono text-lg font-semibold text-slate-800">{timeLabel(todayRecord.jam_masuk)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs text-slate-500">Pulang</p>
                    <p className="mt-1 font-mono text-lg font-semibold text-slate-800">{todayRecord.jam_keluar ? timeLabel(todayRecord.jam_keluar) : "-"}</p>
                  </div>
                </div>
              </section>
            )}

            {/* <div className="pt-1">
              <h3 className="mb-3 text-sm font-semibold text-slate-800">Pengajuan Lainnya</h3>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => onNavigate("izinlist")} className="flex flex-col items-start gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:bg-slate-50">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-100"><FileText className="h-5 w-5 text-blue-600" /></div>
                  <div><p className="text-sm font-semibold text-slate-800">Izin</p><p className="mt-0.5 text-[11px] text-slate-500">Ajukan izin</p></div>
                </button>
                <button onClick={() => onNavigate("lemburlist")} className="flex flex-col items-start gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:bg-slate-50">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-100"><Timer className="h-5 w-5 text-orange-600" /></div>
                  <div><p className="text-sm font-semibold text-slate-800">Lembur</p><p className="mt-0.5 text-[11px] text-slate-500">Ajukan lembur</p></div>
                </button>
              </div>
            </div> */}

            {todayIzinList.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-blue-600" /><p className="text-sm font-semibold text-slate-800">Izin Hari Ini</p></div>
                  <button onClick={() => onNavigate("izin")} className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"><Eye className="h-3.5 w-3.5" /> Lihat Semua</button>
                </div>
                <div className="space-y-2 mt-3">
                  {todayIzinList.map((data) => (
                    <div key={data.id} className="rounded-xl border bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-xs font-medium text-slate-700 line-clamp-1">{data.keterangan || "Izin"}</p>
                          <p className="mt-1 text-xs text-slate-600">{new Date(data.startDate).toLocaleDateString("id-ID", { day: 'numeric', month: 'long', year: 'numeric'})}</p>
                          <p className="mt-1.5 text-[10px] text-slate-400">{new Date(data.submittedAt).toLocaleString("id-ID")}</p>
                        </div>
                        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${statusBadge(data.penyetujuan_aktivitas)}`}>{formatStatus(data.penyetujuan_aktivitas)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {todayLemburList.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Clock3 className="h-4 w-4 text-orange-600" /><p className="text-sm font-semibold text-slate-800">Lembur Hari Ini</p></div>
                  <button onClick={() => onNavigate("lembur")} className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"><Eye className="h-3.5 w-3.5" /> Lihat Semua</button>
                </div>
                <div className="space-y-2 mt-3">
                  {todayLemburList.map((data) => (
                    <div key={data.id} className="rounded-xl border bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-xs font-medium text-slate-700">{data.nama_kegiatan}</p>
                          <p className="mt-1 text-xs text-slate-600 font-mono">{data.mulai?.slice(0,5)} - {data.sampai?.slice(0,5)} · {new Date(data.tanggal).toLocaleDateString("id-ID", { day: 'numeric', month: 'short'})}</p>
                        </div>
                        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${statusBadge(data.status)}`}>{formatStatus(data.status)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {todayIzinList.length === 0 && todayLemburList.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-center">
                <p className="text-xs text-slate-500">Tidak ada pengajuan izin atau lembur hari ini.</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
